import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mantine/core';
import { useRidesAvailabilityService, RIDES_SEX, RIDES_ERROR_CODES } from '@actschurch/ccms-sdk';
import { driverMissingStep } from '@/store/ridesWizard';
import { useDriverProfile, useInvalidateMe } from '@/lib/me';
import { ridesKeys } from '@/lib/queryKeys';
import { formatShortDate, formatTime } from '@/lib/format';
import { hasErrorCode } from '@/lib/copy';
import { useCopy } from '@/lib/settings';
import { StepHeader } from '@/components/ui/StepHeader';
import { StickyActionBar } from '@/components/ui/StickyActionBar';
import { SectionCard } from '@/components/ui/SectionCard';
import { Callout } from '@/components/ui/Callout';
import { LoadingState } from '@/components/ui/LoadingState';
import { PlateChip } from '@/components/rides/PlateChip';
import { ErrorAlert } from '@/components/rides/ErrorAlert';
import { eventTimeLabel } from '@/components/rides/EventPicker';
import { ReadinessList, useDriverReadiness, type ReadyItem } from '@/components/flows/DriverReadiness';
import { carWithColour } from '@/components/flows/LiftCard';
import { defaultDeparture, useOfferFlow } from './_shared';
import '@/styles/flows.css';

/** Send the driver back here after fixing something on the checklist. */
const withReturn = (to: string) => `${to}${to.includes('?') ? '&' : '?'}next=/drive/summary`;

const GATE_CODES = [
  RIDES_ERROR_CODES.DriverPhotoRequired,
  RIDES_ERROR_CODES.DocumentsRequired,
  RIDES_ERROR_CODES.DeclarationRequired,
  RIDES_ERROR_CODES.LicenceExperienceRequired,
  RIDES_ERROR_CODES.EmergencyContactRequired,
];

/**
 * Offer a lift, the last step: every choice with a "Change" link, the car riders will look for,
 * what happens next, and anything that still stops riders from seeing the lift (with a link to fix
 * it, then straight back here). One lift is created per service (POST availability).
 */
export default function DriveSummaryPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useCopy();
  const f = useOfferFlow();
  const { driver, resetDriver, settings, chosen, col, drop, svc } = f;
  const profile = useDriverProfile();
  const readiness = useDriverReadiness();
  const invalidate = useInvalidateMe();
  const availability = useRidesAvailabilityService();

  const list = useMutation({
    mutationFn: () =>
      availability.create({
        eventIDs: driver.eventIds,
        collectionLocationID: driver.collectionLocationId!,
        dropOffLocationID: driver.dropOffLocationId!,
        serviceTimeID: driver.serviceTimeId,
        departureTime: driver.departureTime,
        seatsTotal: driver.seats,
        notes: settings.allowDriverNotes ? driver.notes.trim() || null : null,
      }),
    onError: (e) => {
      // A gate the cached profile didn't know about (settings changed): refresh the checklist.
      if (GATE_CODES.some((c) => hasErrorCode(e, c))) invalidate();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ridesKeys.availability.all });
      qc.invalidateQueries({ queryKey: ridesKeys.me.summary });
      qc.invalidateQueries({ queryKey: ['rides', 'events'] });
      resetDriver();
      navigate('/drive/listings?listed=1', { replace: true });
    },
  });

  const missing = driverMissingStep(driver, 'summary');
  if (missing) return <Navigate to={missing} replace />;
  if (!profile.isLoading && !profile.error && profile.data === null) return <Navigate to="/drive/profile" replace />;

  const d = profile.data;
  const gateRefused = GATE_CODES.some((c) => hasErrorCode(list.error, c));
  const todos: ReadyItem[] = readiness.todos.map((i) => ({ ...i, to: i.to ? withReturn(i.to) : null }));
  const blocked = todos.length > 0;
  // The API only creates lifts for a fully checked driver: waiting for a check also stops it.
  const waitingItems = readiness.items.filter((i) => i.state === 'waiting');
  const waiting = !blocked && (readiness.overall === 'waiting' || (!!d && !d.isFullyVerified));
  const def = defaultDeparture(chosen, svc?.startTime ?? null, settings);
  const lead = settings.defaultDepartureLeadMinutes;
  const leaves = driver.departureTime
    ? formatTime(driver.departureTime)
    : def
      ? `${def} (${lead} minutes before the service)`
      : `${lead} minutes before each service or event`;
  const nextLines = t('drive.review_next').split('\n').filter(Boolean);
  const count = chosen.length || driver.eventIds.length;

  const onPrimary = () => {
    if (waiting) { navigate('/home'); return; }
    if (blocked) {
      const first = todos.find((i) => i.to);
      if (first?.to) navigate(first.to);
      return;
    }
    list.mutate();
  };

  return (
    <div className="rides-main rides-flow">
      <StepHeader step={f.stepOf('review')} total={f.total} title="Check your lift" lede={t('drive.review_intro')} backTo="/drive/route?q=details" />
      <div className="rides-stepbody">
      {profile.isLoading || readiness.loading ? (
        <LoadingState label="Loading your lift" variant="rows" count={6} />
      ) : (
        <div className="rides-flow-stack">
          {blocked && (
            <Callout tone={gateRefused ? 'bad' : 'warn'} live={gateRefused} title="Before riders can see this lift">
              {todos.length === 1 ? 'Please finish this first:' : 'Please finish these first:'}
            </Callout>
          )}
          {blocked && (
            <SectionCard flush>
              <ReadinessList items={todos} />
            </SectionCard>
          )}
          {waiting && <Callout tone="info" title="Your checks are not finished yet">{t('drive.review_waiting')}</Callout>}
          {waiting && waitingItems.length > 0 && (
            <SectionCard flush>
              <ReadinessList items={waitingItems} interactive={false} />
            </SectionCard>
          )}

          <SectionCard title="Your lift" flush>
            <ul className="rides-review">
              <li>
                <span className="txt">
                  <span className="k">{count === 1 ? 'Service' : `Services (${count})`}</span>
                  <span className="v">
                    {chosen.map((e) => (
                      <span key={e.eventID} style={{ display: 'block' }}>
                        {e.name}, {formatShortDate(e.date)}
                        {!e.usesCampusServiceTimes && eventTimeLabel(e) ? <small>Starts at {eventTimeLabel(e)}</small> : null}
                      </span>
                    ))}
                  </span>
                </span>
                <Link className="rides-linkbtn chg" to="/drive/events">Change</Link>
              </li>
              <li>
                <span className="txt"><span className="k">Pick-up campus</span><span className="v">{col?.name ?? ''}</span></span>
                <Link className="rides-linkbtn chg" to="/drive/route">Change</Link>
              </li>
              <li>
                <span className="txt">
                  <span className="k">Driving to</span>
                  <span className="v">{drop?.name ?? ''}{f.needsService && <small>{svc ? `For the ${formatTime(svc.startTime)} service` : 'Any service'}</small>}</span>
                </span>
                <Link className="rides-linkbtn chg" to={f.needsService ? '/drive/route?q=service' : '/drive/route?q=to'}>Change</Link>
              </li>
              <li>
                <span className="txt"><span className="k">Leaves at</span><span className="v">{leaves}</span></span>
                <Link className="rides-linkbtn chg" to="/drive/route?q=details">Change</Link>
              </li>
              <li>
                <span className="txt"><span className="k">Seats for riders</span><span className="v">{driver.seats}</span></span>
                <Link className="rides-linkbtn chg" to="/drive/route?q=details">Change</Link>
              </li>
              {settings.allowDriverNotes && driver.notes.trim() && (
                <li>
                  <span className="txt"><span className="k">Note for riders</span><span className="v">“{driver.notes.trim()}”</span></span>
                  <Link className="rides-linkbtn chg" to="/drive/route?q=details">Change</Link>
                </li>
              )}
              {d && (
                <li>
                  <span className="txt">
                    <span className="k">Your car</span>
                    <span className="v">{carWithColour(d.vehicle)}<span style={{ display: 'block', marginTop: 6 }}><PlateChip plate={d.vehicle.plate} /></span></span>
                  </span>
                  <Link className="rides-linkbtn chg" to={withReturn('/drive/profile?part=car')}>Change</Link>
                </li>
              )}
            </ul>
          </SectionCard>

          <SectionCard title="What happens next">
            <ol className="rides-nextlist">
              {nextLines.map((line) => <li key={line}>{line}</li>)}
            </ol>
            <p className="rides-help" style={{ marginTop: 12 }}>{t('driver.bring_id_callout')}</p>
            {settings.femaleDriverRuleEnabled && d?.sexID === RIDES_SEX.Female && <p className="rides-help" style={{ marginTop: 8 }}>{t('driver.safety_callout')}</p>}
          </SectionCard>

          {!gateRefused && <ErrorAlert error={list.error} title="We couldn't save your lift" />}
        </div>
      )}
      </div>
      {!profile.isLoading && !readiness.loading && (
        <StickyActionBar>
          <Button fullWidth loading={list.isPending} onClick={onPrimary}>
            {waiting ? 'Back to Home' : blocked ? 'Finish getting ready' : count > 1 ? `Offer these ${count} lifts` : 'Offer this lift'}
          </Button>
        </StickyActionBar>
      )}
    </div>
  );
}
